import { type FC, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import { newrelic } from "@jti/fe-clients";
import { BREAKPOINT_SMALL_LARGE } from "@jti/fe-common";
import type { AddressStored } from "@jti/fe-common/types";
import { AddressSelectCard, Dialog } from "@jti/ui";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./ShippingAddressDialog.module.scss";

export type ShippingAddressDialogProps = {
  open: boolean;
  addresses: AddressStored[];
  preselectedAddress?: AddressStored;
  onCreateNew: () => void;
  onCancel: () => void;
  onConfirm: (address: AddressStored) => void;
  isOnSubmitLoading?: boolean;
};

export const ShippingAddressDialog: FC<ShippingAddressDialogProps> = ({
  open,
  addresses,
  onCreateNew,
  onCancel,
  onConfirm,
  preselectedAddress,
  isOnSubmitLoading = false,
}) => {
  const { __ } = useTranslations();
  const [selectedAddressId, setSelectedAddressId] = useState<string>(preselectedAddress?.id ?? "");

  const isDesktopView = useMediaQuery({ minWidth: BREAKPOINT_SMALL_LARGE });

  const onAddressConfirm = () => {
    const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId);

    if (selectedAddress) {
      onConfirm(selectedAddress);
      return;
    }

    toast.error(__("cart.checkout.shipping_address.edit_dialog.select_address_error", [selectedAddressId]));
    newrelic.noticeError(new Error("No address selected in ShippingAddressDialog"));
  };

  return (
    <Dialog closable={false} isOpened={open} className={styles.dialog}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{__("cart.checkout.shipping_address.edit_dialog.title")}</h2>
          <h3 className={styles.subtitle}>{__("cart.checkout.shipping_address.edit_dialog.subTitle")}</h3>
        </div>

        <div
          className={styles.cards}
          role="radiogroup"
          aria-label={__("cart.checkout.shipping_address.edit_dialog.title")}
        >
          {addresses?.map((address) => (
            <AddressSelectCard
              key={address.id}
              address={address}
              onSelect={setSelectedAddressId}
              isSelected={selectedAddressId === address.id}
            />
          ))}
          {!isDesktopView && (
            <Button variant="text" onClick={onCreateNew}>
              {__("cart.checkout.shipping_address.edit_dialog.create_new")}
            </Button>
          )}
        </div>

        {isDesktopView && (
          <Button variant="text" onClick={onCreateNew} size="lg">
            {__("cart.checkout.shipping_address.edit_dialog.create_new")}
          </Button>
        )}

        <div className={styles.footer}>
          <Button
            variant="primary"
            size={isDesktopView ? "lg" : "sm"}
            fullWidth={!isDesktopView}
            onClick={onAddressConfirm}
            pending={isOnSubmitLoading}
          >
            {__("select_item_dialog.confirm")}
          </Button>
          <Button variant="secondary" size={isDesktopView ? "lg" : "sm"} fullWidth={!isDesktopView} onClick={onCancel}>
            {__("select_item_dialog.cancel")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
